<template>
  <v-app-bar
    color="surface"
    elevation="1"
    density="comfortable"
  >
    <v-app-bar-nav-icon
      @click="$emit('toggle-drawer')"
      class="d-md-none"
    />

    <v-toolbar-title class="d-none d-sm-flex align-center">
      <v-breadcrumbs
        :items="breadcrumbs"
        density="compact"
        class="pa-0"
      >
        <template #divider>
          <v-icon size="small">mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>
    </v-toolbar-title>

    <v-spacer />

    <!-- Search -->
    <v-menu
      v-model="searchMenu"
      :close-on-content-click="false"
      offset-y
      max-width="480"
      min-width="480"
      eager
    >
      <template #activator="{ props: menuProps }">
        <v-text-field
          v-model="searchQuery"
          v-bind="menuProps"
          prepend-inner-icon="mdi-magnify"
          :append-inner-icon="searchQuery ? 'mdi-close' : undefined"
          @click:append-inner="clearSearch"
          placeholder="Search…"
          variant="filled"
          density="compact"
          hide-details
          single-line
          class="mr-4 search-field"
          style="max-width: 300px; min-width: 200px;"
          @focus="onSearchFocus"
          @keydown.esc="clearSearch"
          @keydown.enter="onSearchEnter"
          autocomplete="off"
        />
      </template>

      <v-card elevation="8" class="search-dropdown">
        <!-- Loading -->
        <div v-if="searchLoading" class="text-center pa-6">
          <v-progress-circular size="28" indeterminate color="primary" />
          <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Searching…</p>
        </div>

        <!-- No results -->
        <div
          v-else-if="searchQuery.length >= 2 && searchResults.length === 0"
          class="text-center pa-6"
        >
          <v-icon size="40" color="grey-lighten-2">mdi-magnify-close</v-icon>
          <p class="text-body-2 text-medium-emphasis mt-2 mb-0">
            No results for <strong>"{{ searchQuery }}"</strong>
          </p>
        </div>

        <!-- Prompt -->
        <div
          v-else-if="searchQuery.length < 2"
          class="text-center pa-6"
        >
          <v-icon size="40" color="grey-lighten-2">mdi-magnify</v-icon>
          <p class="text-body-2 text-medium-emphasis mt-2 mb-0">Type at least 2 characters</p>
        </div>

        <!-- Results grouped by type -->
        <v-list v-else density="compact" class="search-results-list py-1">
          <template v-for="row in searchFlat">
            <v-list-subheader
              v-if="row.isHeader"
              :key="`hdr-${row.type}`"
              class="text-caption font-weight-bold text-uppercase"
            >
              {{ row.label }}
            </v-list-subheader>
            <v-list-item
              v-else
              :key="`${row.type}-${row.id}`"
              rounded="lg"
              class="search-result-item mx-2 mb-1"
              @click="navigateToResult(row)"
            >
              <template #prepend>
                <v-avatar :color="row.color" size="32" class="mr-3">
                  <v-icon color="white" size="16">{{ row.icon }}</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title>{{ row.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ row.meta || row.subtitle }}</v-list-item-subtitle>
              <template #append>
                <v-icon size="16" color="grey">mdi-arrow-top-left</v-icon>
              </template>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Notifications -->
    <v-menu
      v-model="notificationMenu"
      offset-y
      max-width="400"
      :close-on-content-click="false"
    >
      <template #activator="{ props }">
        <v-btn
          icon
          variant="text"
          class="mr-2"
          v-bind="props"
        >
          <v-badge
            v-if="notificationsStore.unreadCount > 0"
            :content="notificationsStore.unreadCount"
            color="error"
            :max="99"
          >
            <v-icon>mdi-bell</v-icon>
          </v-badge>
          <v-icon v-else>mdi-bell-outline</v-icon>
        </v-btn>
      </template>

      <v-card class="notification-dropdown" elevation="8">
        <v-card-title class="d-flex justify-space-between align-center pa-4 border-b">
          <span class="text-h6 font-weight-medium">Notifications</span>
          <div class="d-flex align-center gap-2">
            <v-btn
              v-if="notificationsStore.unreadCount > 0"
              size="small"
              variant="text"
              @click="markAllAsRead"
              :loading="notificationsStore.markingAllRead"
            >
              Mark all read
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              @click="openNotificationSettings"
            >
              <v-icon>mdi-cog</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <div class="notification-list" style="max-height: 400px; overflow-y: auto;">
          <div v-if="notificationsStore.loading" class="text-center pa-4">
            <v-progress-circular size="24" indeterminate />
            <p class="text-body-2 mt-2 mb-0">Loading notifications...</p>
          </div>

          <div v-else-if="notificationsStore.items.length === 0" class="text-center pa-8">
            <v-icon size="48" color="grey-lighten-2">mdi-bell-off-outline</v-icon>
            <p class="text-body-1 text-medium-emphasis mt-3 mb-0">No notifications yet</p>
          </div>

          <div v-else>
            <div
              v-for="notification in notificationsStore.items"
              :key="notification.id"
              class="notification-item"
              :class="{ 'unread': !notification.is_read }"
              @click="handleNotificationClick(notification)"
            >
              <div class="d-flex align-start pa-3">
                <v-avatar
                  :color="notificationsStore.colorFor(notification.type)"
                  size="32"
                  class="mr-3"
                >
                  <v-icon color="white" size="16">
                    {{ notificationsStore.iconFor(notification.type) }}
                  </v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="d-flex justify-space-between align-start mb-1">
                    <p class="text-body-2 font-weight-medium mb-1">
                      {{ notification.title }}
                    </p>
                    <div class="d-flex align-center gap-1">
                      <span class="text-caption text-medium-emphasis">
                        {{ notificationsStore.formatTime(notification.created_at) }}
                      </span>
                      <v-menu offset-y>
                        <template #activator="{ props }">
                          <v-btn
                            icon
                            size="x-small"
                            variant="text"
                            v-bind="props"
                            @click.stop
                          >
                            <v-icon size="16">mdi-dots-horizontal</v-icon>
                          </v-btn>
                        </template>
                        <v-list density="compact">
                          <v-list-item
                            v-if="!notification.is_read"
                            @click="notificationsStore.markRead(notification)"
                          >
                            <v-list-item-title>Mark as read</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-else
                            @click="notificationsStore.markUnread(notification)"
                          >
                            <v-list-item-title>Mark as unread</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            @click="deleteNotification(notification)"
                            class="text-error"
                          >
                            <v-list-item-title>Delete</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </div>

                  <p class="text-body-2 text-medium-emphasis mb-2">
                    {{ notification.message }}
                  </p>

                  <div v-if="notification.action_url" class="notification-actions">
                    <v-btn
                      size="small"
                      color="primary"
                      variant="text"
                      @click.stop="navigateToAction(notification)"
                    >
                      {{ notification.action_text || 'View Details' }}
                    </v-btn>
                  </div>

                  <div class="d-flex align-center mt-2">
                    <v-chip
                      :color="notificationsStore.chipColorFor(notification.type)"
                      size="x-small"
                      variant="tonal"
                      class="mr-2"
                    >
                      {{ notificationsStore.labelFor(notification.type) }}
                    </v-chip>
                    <v-chip
                      v-if="notification.priority === 'high'"
                      color="error"
                      size="x-small"
                      variant="flat"
                    >
                      High Priority
                    </v-chip>
                    <v-chip
                      v-if="!notification.is_read"
                      color="primary"
                      size="x-small"
                      variant="flat"
                    >
                      New
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn
            variant="text"
            block
            @click="viewAllNotifications"
            append-icon="mdi-arrow-right"
          >
            View All Notifications
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <!-- Theme Toggle -->
    <v-btn
      icon
      variant="text"
      @click="toggleTheme"
      class="mr-2"
    >
      <v-icon>
        {{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}
      </v-icon>
    </v-btn>

    <!-- User Menu -->
    <v-menu>
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="text-none"
        >
          <v-avatar size="32" class="mr-2">
            <v-img
              v-if="user.profile_image"
              :src="getFileUrl(user.profile_image)"
              :alt="user.full_name"
            />
            <span v-else class="text-h6">
              {{ user.first_name?.[0] }}{{ user.last_name?.[0] }}
            </span>
          </v-avatar>
          <span class="d-none d-sm-inline">{{ user.first_name }}</span>
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item prepend-icon="mdi-account-circle">
          <v-list-item-title>{{ user.full_name }}</v-list-item-title>
          <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
        </v-list-item>
        <v-divider />
        <v-list-item title="Profile" prepend-icon="mdi-account" to="/profile" />
        <v-list-item
          v-if="user.is_admin"
          title="Application Settings"
          prepend-icon="mdi-cog"
          to="/settings"
        />
        <v-divider />
        <v-list-item title="Sign Out" prepend-icon="mdi-logout" @click="handleLogout" />
      </v-list>
    </v-menu>

  </v-app-bar>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useNotificationsStore } from '@/stores'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import { useTheme } from 'vuetify'
import { useToast } from 'vue-toastification'
import api, { getFileUrl } from '@/services/api'

export default {
  name: 'AppTopBar',
  emits: ['toggle-drawer'],
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const notificationsStore = useNotificationsStore()
    const { breadcrumbTitle, clearBreadcrumbTitle } = useBreadcrumb()
    const theme = useTheme()
    const toast = useToast()

    const searchQuery = ref('')
    const searchMenu = ref(false)
    const searchLoading = ref(false)
    const searchResults = ref([])
    let searchDebounce = null
    const notificationMenu = ref(false)
    const pollInterval = ref(null)

    const user = computed(() => authStore.user || {})
    const isDark = computed(() => theme.global.current.value.dark === true)

    const breadcrumbs = computed(() => {
      const segments = route.path.split('/').filter(Boolean)
      const crumbs = [{ title: 'Home', to: '/' }]
      let path = ''
      segments.forEach(segment => {
        path += `/${segment}`
        const isId = /^\d+$/.test(segment)
        crumbs.push({
          title: isId
            ? (breadcrumbTitle.value || `#${segment}`)
            : segment.charAt(0).toUpperCase() + segment.slice(1),
          to: path
        })
      })
      return crumbs
    })

    // Clear stale title when navigating away from a detail page
    watch(() => route.path, () => {
      clearBreadcrumbTitle()
    })

    // ─── Search ───────────────────────────────────────────────────────────────
    const searchTypeLabel = (type) => ({
      resolution: 'Resolutions',
      meeting: 'Meetings',
      document: 'Documents',
      committee: 'Committees',
      member: 'Members',
    }[type] || type)

    /**
     * Flatten grouped results into a single array with header sentinel objects
     * so we can use a single v-for without nested <template v-for> key issues.
     */
    const searchFlat = computed(() => {
      const order = ['resolution', 'meeting', 'document', 'committee', 'member']
      const grouped = searchResults.value.reduce((acc, item) => {
        if (!acc[item.type]) acc[item.type] = []
        acc[item.type].push(item)
        return acc
      }, {})
      const flat = []
      order.forEach(type => {
        if (!grouped[type]?.length) return
        flat.push({ isHeader: true, type, label: searchTypeLabel(type) })
        grouped[type].forEach(item => flat.push({ ...item, isHeader: false }))
      })
      return flat
    })

    const runSearch = async (q) => {
      if (q.length < 2) { searchResults.value = []; return }
      searchLoading.value = true
      try {
        const response = await api.get('/search/', { params: { q } })
        searchResults.value = response.data.results || []
      } catch {
        searchResults.value = []
      } finally {
        searchLoading.value = false
      }
    }

    const onSearchFocus = () => {
      if (searchQuery.value.length >= 2) searchMenu.value = true
    }

    const clearSearch = () => {
      searchQuery.value = ''
      searchResults.value = []
      searchMenu.value = false
    }

    const navigateToResult = (item) => {
      clearSearch()
      router.push(item.route)
    }

    const onSearchEnter = () => {
      if (searchResults.value.length === 1) navigateToResult(searchResults.value[0])
    }

    watch(searchQuery, (val) => {
      clearTimeout(searchDebounce)
      if (val.length >= 2) {
        searchMenu.value = true
        searchDebounce = setTimeout(() => runSearch(val), 300)
      } else {
        searchResults.value = []
        searchMenu.value = val.length > 0  // keep open to show the prompt
      }
    })

    // ─── Notification actions (UI wrappers — add toast feedback) ─────────────
    const markAllAsRead = async () => {
      await notificationsStore.markAllRead()
      toast.success('All notifications marked as read')
    }

    const deleteNotification = async (notification) => {
      await notificationsStore.remove(notification)
      toast.success('Notification deleted')
    }

    const handleNotificationClick = (notification) => {
      if (!notification.is_read) notificationsStore.markRead(notification)
      if (notification.action_url) navigateToAction(notification)
    }

    const navigateToAction = (notification) => {
      if (notification.action_url) {
        router.push(notification.action_url)
        notificationMenu.value = false
      }
    }

    const viewAllNotifications = () => {
      router.push('/notifications')
      notificationMenu.value = false
    }

    const openNotificationSettings = () => {
      router.push('/settings/notifications')
      notificationMenu.value = false
    }

    // Load notifications when the menu opens (cache-aware)
    watch(notificationMenu, (isOpen) => {
      if (isOpen) notificationsStore.fetch()
    })

    // ─── Theme ───────────────────────────────────────────────────────────────
    const toggleTheme = () => {
      const newTheme = isDark.value ? 'customTheme' : 'darkTheme'
      theme.change(newTheme)
      localStorage.setItem('theme', newTheme)
    }

    // ─── Auth ────────────────────────────────────────────────────────────────
    const handleLogout = async () => {
      try {
        await authStore.logout()
        toast.success('Logged out successfully')
        router.push('/auth/login')
      } catch {
        toast.error('Logout failed')
      }
    }

    onMounted(() => {
      // Restore saved theme preference
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme) theme.change(savedTheme)

      // Initial fetch + background polling
      notificationsStore.fetch()
      pollInterval.value = setInterval(
        () => notificationsStore.fetch(),
        notificationsStore.POLL_INTERVAL
      )
    })

    onBeforeUnmount(() => {
      if (pollInterval.value) clearInterval(pollInterval.value)
    })

    return {
      searchQuery,
      searchMenu,
      searchLoading,
      searchResults,
      searchFlat,
      searchTypeLabel,
      clearSearch,
      navigateToResult,
      onSearchFocus,
      onSearchEnter,
      getFileUrl,
      notificationMenu,
      notificationsStore,
      user,
      isDark,
      breadcrumbs,
      toggleTheme,
      handleLogout,
      markAllAsRead,
      deleteNotification,
      handleNotificationClick,
      navigateToAction,
      viewAllNotifications,
      openNotificationSettings
    }
  }
}
</script>

<style scoped lang="scss">
.search-field {
  .v-field {
    border-radius: 8px !important;
  }
}

.search-dropdown {
  min-width: 460px;
  max-width: 480px;
  border-radius: 12px !important;
  overflow: hidden;

  .search-results-list {
    max-height: 420px;
    overflow-y: auto;
  }

  .search-result-item {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.06);
    }
  }
}

.notification-dropdown {
  min-width: 380px;
  max-width: 420px;

  .border-b {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }
}

.notification-list {
  .notification-item {
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }

    &:last-child {
      border-bottom: none;
    }

    &.unread {
      background-color: rgba(var(--v-theme-primary), 0.02);
      border-left: 3px solid rgb(var(--v-theme-primary));

      &:hover {
        background-color: rgba(var(--v-theme-primary), 0.06);
      }
    }
  }
}

@media (max-width: 960px) {
  .notification-dropdown {
    min-width: 320px;
    max-width: 360px;
  }
}

@media (max-width: 600px) {
  .notification-dropdown {
    min-width: 280px;
    max-width: 320px;
  }
}

.v-theme--dark {
  .notification-dropdown .border-b {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .notification-list .notification-item {
    border-bottom-color: rgba(255, 255, 255, 0.06);

    &:hover {
      background-color: rgba(255, 255, 255, 0.04);
    }

    &.unread {
      background-color: rgba(var(--v-theme-primary), 0.08);

      &:hover {
        background-color: rgba(var(--v-theme-primary), 0.12);
      }
    }
  }
}

@keyframes notificationPulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(var(--v-theme-primary), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0); }
}

.v-btn .v-badge .v-badge__badge {
  animation: notificationPulse 2s infinite;
}
</style>
